import { t } from 'i18next';
import type { Platform } from '../../context/env-context';
import type {
  CustomerGroupBuilderSettingsState,
  SettingAddressManagementState,
  SettingMultiStockState,
  SettingOrderQuantityLimitsState,
  SettingOrderTotalLimitsState,
  SettingPaymentMethodsState,
  SettingPriceListState,
  SettingQuotingEngineState,
  SettingResponseState,
  SettingStockDisplayState,
} from './types/customer-group.schema';
import type { InheritList } from './types/customer-group-response';
import type { ExcludedSettingComponentMapKeys, SettingKey } from './types/settings.types';

export const LIST_DELIMITER = ', ';

export const CUSTOMER_GROUP_BASE_TAG = 'b2b';

export const CUSTOMER_GROUP_BASE_SLUG = 'base';
// Mirrors the spark-dash-api "Customer Group Request" schema.
export const CUSTOMER_GROUP_NAME_MAX_LENGTH = 256;
export const CUSTOMER_GROUP_SLUG_MAX_LENGTH = 128;

const SETTING_NAMES = {
  PRICE_LISTS: t('features:customer_group.components.price_lists_card.title'),
  PAYMENT_METHODS: t('features:customer_group.components.payment_methods_card.title'),
  QUOTING_ENGINE: t('features:customer_group.components.quoting_engine_card.title'),
  ORDER_QUANTITY_LIMIT: t('features:customer_group.components.order_quantity_limit_card.title'),
  ORDER_TOTAL_LIMITS: t('features:customer_group.components.order_total_limits_card.title'),
  ADDRESS_MANAGEMENT: t('features:customer_group.components.address_management_card.title'),
  CREDIT_SETTINGS: t('features:customer_group.components.credit_settings_card.title'),
  STOCK_DISPLAY_SETTINGS: t('features:customer_group.components.stock_display_card.title'),
  STOCK_LOCATION: t('features:customer_group.components.stock_location_card.title'),
  DISABLE_CHECKOUT: t('features:customer_group.components.allow_checkout_card.title'),
} as const;

export const SETTING_KEY_TO_LABEL: Record<SettingKey, string> = {
  priceListSelection: SETTING_NAMES.PRICE_LISTS,
  orderTotalsValidation: SETTING_NAMES.ORDER_TOTAL_LIMITS,
  orderItemQuantityValidation: SETTING_NAMES.ORDER_QUANTITY_LIMIT,
  allowedPaymentMethods: SETTING_NAMES.PAYMENT_METHODS,
  allowAddressEditing: SETTING_NAMES.ADDRESS_MANAGEMENT,
  allowDefaultBillingModification: SETTING_NAMES.ADDRESS_MANAGEMENT,
  paymentOnAccountEnforceCreditLimit: SETTING_NAMES.CREDIT_SETTINGS,
  multiLocationStockConfig: SETTING_NAMES.STOCK_LOCATION,
  useMultiLocationStock: SETTING_NAMES.STOCK_LOCATION,
  stockDisplay: SETTING_NAMES.STOCK_DISPLAY_SETTINGS,
  quotingEngine: SETTING_NAMES.QUOTING_ENGINE,
  allowCheckout: SETTING_NAMES.DISABLE_CHECKOUT,
} as const;

export const TABLE_OTHER_SETTINGS: InheritList = [
  'allowAddressEditing',
  'allowDefaultBillingModification',
  'allowCheckout',
  'quotingEngine',
  'paymentOnAccountEnforceCreditLimit',
  'multiLocationStockConfig',
  'stockDisplay',
] as const;

export const SETTINGS_ORDER: Array<SettingKey> = [
  'priceListSelection',
  'allowedPaymentMethods',
  'orderItemQuantityValidation',
  'orderTotalsValidation',
  'allowAddressEditing',
  'allowDefaultBillingModification',
  'stockDisplay',
  'multiLocationStockConfig',
  'useMultiLocationStock',
  'paymentOnAccountEnforceCreditLimit',
  'quotingEngine',
  'allowCheckout',
] as const;

export const DEFAULT_SETTINGS: CustomerGroupBuilderSettingsState = SETTINGS_ORDER.reduce(
  (acc: CustomerGroupBuilderSettingsState, key: SettingKey) => {
    acc[key] = undefined;
    return acc;
  },
  {},
);

export const INITIAL_CUSTOMER_GROUP = {
  name: '',
  slug: '',
  parent_slug: CUSTOMER_GROUP_BASE_SLUG,
  hasChildren: false,
  settings: DEFAULT_SETTINGS,
};

const COLUMN_IDS = {
  name: 'name',
  tags: 'tags',
  priceLists: 'priceLists',
  paymentMethods: 'paymentMethods',
  orderLimits: 'orderLimits',
  otherSettings: 'otherSettings',
  actions: 'actions',
} as const;

export type ColumnId = (typeof COLUMN_IDS)[keyof typeof COLUMN_IDS];

type TableColumn = {
  id: ColumnId;
  labelKey: string | undefined;
  alwaysVisible: boolean | undefined;
};

export const TABLE_COLUMNS = [
  {
    id: 'name',
    labelKey: 'features:customer_group.components.customer_groups_table.headings.name',
    alwaysVisible: true,
  },
  {
    id: 'tags',
    labelKey: 'features:customer_group.components.customer_groups_table.headings.tags',
    alwaysVisible: undefined,
  },
  {
    id: 'priceLists',
    labelKey: 'features:customer_group.components.customer_groups_table.headings.price_lists',
    alwaysVisible: undefined,
  },
  {
    id: 'paymentMethods',
    labelKey: 'features:customer_group.components.customer_groups_table.headings.payment_methods',
    alwaysVisible: undefined,
  },
  {
    id: 'orderLimits',
    labelKey: 'features:customer_group.components.customer_groups_table.headings.order_limits',
    alwaysVisible: undefined,
  },
  {
    id: 'otherSettings',
    labelKey: 'features:customer_group.components.customer_groups_table.headings.other_settings',
    alwaysVisible: undefined,
  },
  { id: 'actions', labelKey: undefined, alwaysVisible: true },
] as const satisfies readonly TableColumn[];

export function resolveColumnHeadingKey(
  column: (typeof TABLE_COLUMNS)[number],
  platform?: Platform,
) {
  if (column.id !== 'tags') return column.labelKey;

  switch (platform) {
    case 'shopify':
    case 'shopify_plus':
      return 'features:customer_group.components.customer_groups_table.headings.tags_shopify' as const;
    case 'magento':
      return 'features:customer_group.components.customer_groups_table.headings.tags_magento' as const;
    case 'wix':
      return 'features:customer_group.components.customer_groups_table.headings.tags_wix' as const;
    default:
      return 'features:customer_group.components.customer_groups_table.headings.tags' as const;
  }
}

export const EXCLUDED_COMPONENT_SETTINGS: Array<ExcludedSettingComponentMapKeys> = [
  'allowDefaultBillingModification',
  'useMultiLocationStock',
] as const;

export const DEFAULT_PAYMENT_METHODS: SettingPaymentMethodsState = {
  payment_by_invoice: true,
  payment_on_account: false,
  upfront_payment: true,
  quote: false,
};

const DEFAULT_ADDRESS_MANAGEMENT: SettingAddressManagementState = {
  allowAddressEditing: true,
  allowDefaultBillingModification: true,
};

export const DEFAULT_QUOTING_ENGINE: SettingQuotingEngineState = {
  customer_create_quote: true,
};

export const DEFAULT_STOCK_DISPLAY: SettingStockDisplayState = {
  show_status: true,
  hide_preorder: false,
  show_units: false,
  max: '100',
  low: '20',
  last: '5',
};

export const DEFAULT_MULTI_STOCK: SettingMultiStockState = {
  useDefault: true,
  allowedStockLocationIds: [],
};
export const DEFAULT_PRICE_LIST_SELECTION: SettingPriceListState = [];
export const DEFAULT_PAYMENT_ON_ACCOUNT_ENFORCE_CREDIT_LIMIT = true;
export const DEFAULT_ALLOW_CHECKOUT = true;
export const DEFAULT_USE_MULTI_LOCATION_STOCK = false;

const DEFAULT_ORDER_TOTALS_VALIDATION: SettingOrderTotalLimitsState = [];
const DEFAULT_ORDER_ITEM_QUANTITY_VALIDATION: SettingOrderQuantityLimitsState = {};

type SettingsDefaultsMapType = {
  readonly [K in keyof SettingResponseState]: SettingResponseState[K];
};

export const SettingsDefaultsMap: SettingsDefaultsMapType = {
  priceListSelection: DEFAULT_PRICE_LIST_SELECTION,
  orderTotalsValidation: DEFAULT_ORDER_TOTALS_VALIDATION,
  orderItemQuantityValidation: DEFAULT_ORDER_ITEM_QUANTITY_VALIDATION,
  allowedPaymentMethods: DEFAULT_PAYMENT_METHODS,
  allowAddressEditing: DEFAULT_ADDRESS_MANAGEMENT,
  allowDefaultBillingModification: DEFAULT_ADDRESS_MANAGEMENT.allowDefaultBillingModification,
  paymentOnAccountEnforceCreditLimit: DEFAULT_PAYMENT_ON_ACCOUNT_ENFORCE_CREDIT_LIMIT,
  stockDisplay: DEFAULT_STOCK_DISPLAY,
  quotingEngine: DEFAULT_QUOTING_ENGINE,
  allowCheckout: DEFAULT_ALLOW_CHECKOUT,
  multiLocationStockConfig: DEFAULT_MULTI_STOCK,
  useMultiLocationStock: DEFAULT_USE_MULTI_LOCATION_STOCK,
};
